'use client';

import { ReactNode, useState } from 'react';
import { DataTestIdEnum } from '@medprev-web-app/datatestid';
import {
  EntityRatingCommentCount,
  Laboratory,
  LaboratoryExam,
  SimilarLaboratoriesResponse,
} from '@medprev-web-app/api-graphql-client';

import { BranchLaboratoryHeader } from './branch-laboratory-header.component';
import { BranchLaboratoryAsideCard } from './branch-laboratory-aside-card.component';
import { LaboratoryOthersLaboratories } from './laboratory-others-laboratories.component';
import { LaboratoryExamsModal } from './laboratory-exams-modal/laboratory-exams-modal.component';
import { useLaboratoryExamSelection } from './laboratory-exams-modal/_hooks/use-laboratory-exam-selection.hook';
import { LaboratorySchedulesModal } from './laboratory-schedules-modal.component';
import { LaboratoryCoverageModal } from './laboratory-coverage-modal.component';

import { ExamsCoverageBanner } from '@/app/(main)/(pages)/_components/exams-coverage/exams-coverage-banner.component';
import {
  SelectedExamsParam,
  useSelectedExamsCoverage,
} from '@/app/(main)/(pages)/(institucional)/(index)/laboratorios/_hooks/use-selected-exams-coverage.hook';
import { buildLaboratoryExamsCoverage } from '@/app/(main)/(pages)/(institucional)/(index)/laboratorios/_utils/build-laboratory-exams-coverage.util';
import { getAnalyticsPlugin } from '@/app/(main)/_components/logger/analytics/get-analytics-plugin';
import { AnalyticsEnum } from '@/types/enums/analytics/analytics.enum';
import { Breadcrumb } from '@/components/ui/breadcrumb.component';
import { Card } from '@/components/ui/card.component';
import { ContentContainer } from '@/components/ui/content-container.component';
import { resolveLaboratoryName } from '@/utils/laboratory/laboratory.util';
import { ErrorBoundary } from '@/app/_components/error-boundary.component';
import { Typography } from '@/components/ui/typography/typography.component';
import { TypographyVariant } from '@/components/ui/typography/typography.type';
import { useNewSchedule } from '@/feature-flags/hooks/use-new-schedule.hook';

interface LaboratoryWrapperProps {
  laboratory: Laboratory;
  tabs: ReactNode;
  tabsWithoutSimilarLaboratories: ReactNode;
  commentsCount?: EntityRatingCommentCount | null;
  exams: LaboratoryExam[] | null;
  similarLaboratories: SimilarLaboratoriesResponse | null;
  selectedExams?: SelectedExamsParam;
}

export function BranchLaboratoryWrapper({
  commentsCount,
  laboratory,
  tabs,
  tabsWithoutSimilarLaboratories,
  exams,
  similarLaboratories,
  selectedExams,
}: LaboratoryWrapperProps) {
  const { enabled: newScheduleEnabled, loading: isFlagLoading } = useNewSchedule();
  const usesLegacyLayout = isFlagLoading || !newScheduleEnabled;

  const laboratoryName = resolveLaboratoryName(laboratory);
  const facilityId = laboratory.id ?? '';

  const { coverage } = useSelectedExamsCoverage(facilityId, selectedExams);
  const selection = useLaboratoryExamSelection(coverage?.offered);
  const [examsModalIsOpen, setExamsModalIsOpen] = useState(false);
  const [schedulesModalIsOpen, setSchedulesModalIsOpen] = useState(false);
  const [coverageModalIsOpen, setCoverageModalIsOpen] = useState(false);

  const showsCoverage = !usesLegacyLayout && Boolean(coverage);
  const showsOthersLaboratories = !usesLegacyLayout && Number(similarLaboratories?.total) > 0;

  const othersLaboratories = similarLaboratories && (
    <>
      <Typography variant={TypographyVariant.MEDIUM_SUBTITLE} as="h2" className="mb-1 text-center">
        Outros postos de coleta
      </Typography>

      <LaboratoryOthersLaboratories
        laboratory={laboratory}
        initialFetchResponse={similarLaboratories}
        align="center"
      />
    </>
  );

  const handleSeeSchedules = () => {
    setExamsModalIsOpen(false);
    setCoverageModalIsOpen(false);
    setSchedulesModalIsOpen(true);
  };

  const handleAddMoreExams = () => {
    setCoverageModalIsOpen(false);
    setExamsModalIsOpen(true);
  };

  const handleViewCoverage = () => {
    if (coverage) {
      getAnalyticsPlugin().track(AnalyticsEnum.LABORATORY_PROFILE_CLICK_COVERAGE_VIEW, {
        nome_laboratorio: laboratoryName,
        exames_total: coverage.total,
        exames_atendidos: coverage.matched,
        cobertura_completa: coverage.isFullCoverage,
      });
    }

    setCoverageModalIsOpen(true);
  };

  const coverageBanner = showsCoverage && coverage
    ? (
        <ExamsCoverageBanner
          matched={coverage.matched}
          total={coverage.total}
          isFullCoverage={coverage.isFullCoverage}
          dataTestId={DataTestIdEnum.BRANCH_LABORATORY_COVERAGE_BANNER}
          onViewCoverage={handleViewCoverage}
        />
      )
    : undefined;

  const breadcrumbsItems = [
    {
      label: 'Rede de atendimento',
      href: '/rede-de-atendimento/',
    },
    {
      label: laboratoryName,
    },
  ];

  return (
    <>
      <Breadcrumb list={breadcrumbsItems} className="hidden sm:block" />
      <Breadcrumb list={breadcrumbsItems} abbreviated className="sm:hidden" />

      <ErrorBoundary errorMessage="Branch Laboratory Wrapper">
        <ContentContainer className="md:py-6 bg-white md:bg-transparent">
          <div className="block mx-auto sm:max-w-lg md:max-w-full md:w-auto xl:flex lg:flex-row lg:items-start gap-6">
            <section className="flex-1">
              <Card className="px-0 border-0 md:border border-gray-200 md:px-10 pt-6 pb-0 md:pt-8 3xl:pt-10  w-full lg:min-w-[37.5rem] 3xl:min-w-[46.5rem]">
                <BranchLaboratoryHeader
                  laboratory={laboratory}
                  commentsCount={commentsCount}
                  coverageBanner={coverageBanner}
                  onSelectExams={usesLegacyLayout ? undefined : () => setExamsModalIsOpen(true)}
                />
                <hr className="mb-3 mt-8 md:hidden" />

                {usesLegacyLayout ? tabs : tabsWithoutSimilarLaboratories}

                {showsOthersLaboratories && (
                  <div className="pb-3 md:pb-8 xl:hidden">
                    <hr className="my-6" />

                    {othersLaboratories}
                  </div>
                )}
              </Card>
            </section>

            {usesLegacyLayout
              ? (
                  <BranchLaboratoryAsideCard
                    laboratory={laboratory}
                    exams={exams}
                    className="hidden xl:block border-gray-200"
                  />
                )
              : showsOthersLaboratories && (
                <Card className="hidden xl:block w-full xl:max-w-lg 2xl:max-w-lg flex-1 rounded-xl px-10 py-8 xl:sticky xl:top-6 border-gray-200">
                  {othersLaboratories}
                </Card>
              )}
          </div>
        </ContentContainer>
      </ErrorBoundary>

      {!usesLegacyLayout && (
        <>
          <LaboratoryExamsModal
            facilityId={facilityId}
            laboratoryName={laboratoryName}
            open={examsModalIsOpen}
            selection={selection}
            onOpenChange={setExamsModalIsOpen}
            onSeeSchedules={handleSeeSchedules}
          />

          <LaboratorySchedulesModal
            laboratory={laboratory}
            procedureIds={selection.selectedIds}
            total={selection.total}
            open={schedulesModalIsOpen}
            onOpenChange={setSchedulesModalIsOpen}
          />

          {coverage && (
            <LaboratoryCoverageModal
              coverage={buildLaboratoryExamsCoverage(laboratoryName, coverage)}
              open={coverageModalIsOpen}
              onOpenChange={setCoverageModalIsOpen}
              onAddMoreExams={handleAddMoreExams}
              onSeeSchedules={handleSeeSchedules}
            />
          )}
        </>
      )}
    </>
  );
}
